import { useState, useEffect, useCallback, useRef } from 'react';
import { useFriendActions } from '@/hooks/useFriendActions';
import { useFriendStore } from '@/stores/useFriendStore';
import { FriendInfo, UpdateFriendParams } from '@/api/types';
import { preventDuplicateClick } from '@/utils/my-utils';
import BaseDialog from './BaseDialog';

interface EditFriendRemarkDialogProps {
  open: boolean;
  onClose: () => void;
  friend: FriendInfo;
  initialFocus?: 'remark' | 'alias';
}

/**
 * 修改好友备注对话框
 * 允许用户修改好友的备注和别名
 */
export default function EditFriendRemarkDialog({
  open,
  onClose,
  friend,
  initialFocus = 'remark'
}: EditFriendRemarkDialogProps) {
  const [remark, setRemark] = useState('');
  const [alias, setAlias] = useState('');
  const [isModified, setIsModified] = useState(false);

  const remarkInputRef = useRef<HTMLInputElement>(null);
  const aliasInputRef = useRef<HTMLInputElement>(null);

  const { updateFriend, isLoading: updating, error: updateError } = useFriendActions();
  const { updateFriendInList } = useFriendStore();

  // 初始化表单数据
  useEffect(() => {
    if (open && friend) {
      setRemark(friend.remark || '');
      setAlias(friend.alias || '');
      setIsModified(false);
    }
  }, [open, friend]);

  // 自动聚焦到指定输入框
  useEffect(() => {
    if (!open) return;
    
    // 延迟聚焦，确保对话框完全打开
    const timer = setTimeout(() => {
      if (initialFocus === 'remark' && remarkInputRef.current) {
        remarkInputRef.current.focus();
      } else if (initialFocus === 'alias' && aliasInputRef.current) {
        aliasInputRef.current.focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [open, initialFocus]);

  // 检查是否有修改
  useEffect(() => {
    const remarkChanged = remark !== (friend.remark || '');
    const aliasChanged = alias !== (friend.alias || '');
    setIsModified(remarkChanged || aliasChanged);
  }, [remark, alias, friend.remark, friend.alias]);

  /**
   * 处理保存
   */
  const handleSave = useCallback(
    preventDuplicateClick(async () => {
      if (!friend || !isModified) return;

      const params: UpdateFriendParams = {
        friend_id: friend.id,
        remark: remark.trim() || undefined,
        alias: alias.trim() || undefined
      };

      const success = await updateFriend(params);
      if (success) {
        // 更新本地store中的好友信息
        updateFriendInList(friend.id, {
          remark: remark.trim() || '',
          alias: alias.trim() || ''
        });
        
        onClose();
      }
    }),
    [friend, isModified, remark, alias, updateFriend, updateFriendInList, onClose]
  );

  /**
   * 处理取消
   */
  const handleCancel = () => {
    if (isModified) {
      const confirmed = confirm('您有未保存的修改，确定要取消吗？');
      if (!confirmed) return;
    }
    onClose();
  };

  /**
   * 处理输入变化
   */
  const handleRemarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRemark(e.target.value);
  };

  const handleAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAlias(e.target.value);
  };

  /**
   * 处理键盘事件
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isModified && !updating) {
      handleSave();
    }
  };

  if (!friend) return null;

  return (
    <BaseDialog
      open={open}
      onClose={handleCancel}
      title="编辑好友信息"
      maxWidth="sm"
    >
      <div className="space-y-6">
        {/* 好友基本信息 */}
        <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
          {friend.avatar ? (
            <img
              src={friend.avatar}
              alt={friend.nickname}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-600 font-medium">
                {friend.nickname?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          )}
          <div>
            <p className="font-medium text-gray-900">{friend.nickname}</p>
            <p className="text-sm text-gray-500">ID: {friend.handle}</p>
          </div>
        </div>

        {/* 备注输入 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            备注
          </label>
          <div className="relative">
            <input
              ref={remarkInputRef}
              type="text"
              placeholder="请输入备注信息"
              value={remark}
              onChange={handleRemarkChange}
              onKeyPress={handleKeyPress}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={50}
            />
            {remark && (
              <button
                type="button"
                onClick={() => setRemark('')}
                className="absolute right-2 top-2 p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">{remark.length}/50</p>
        </div>

        {/* 别名输入 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            别名
          </label>
          <div className="relative">
            <input
              ref={aliasInputRef}
              type="text"
              placeholder="请输入别名"
              value={alias}
              onChange={handleAliasChange}
              onKeyPress={handleKeyPress}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={30}
            />
            {alias && (
              <button
                type="button"
                onClick={() => setAlias('')}
                className="absolute right-2 top-2 p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">{alias.length}/30</p>
        </div>

        {/* 说明文本 */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>备注：</strong>仅自己可见的备注信息
          </p>
          <p className="text-sm text-blue-800 mt-1">
            <strong>别名：</strong>在好友列表中显示的名称，会替代原昵称
          </p>
        </div>

        {/* 错误信息 */}
        {updateError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{updateError}</p>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 border border-gray-300 hover:border-gray-400 rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={updating || !isModified}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {updating ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </BaseDialog>
  );
}
